<template>
  <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 mb-8">
    <div class="flex justify-between items-center mb-6">
        <div>
            <h2 class="text-xl font-bold text-slate-800 dark:text-white">Events Overview</h2>
            <p class="text-sm text-slate-500">Pipeline Ingestion & Dispatch Metrics</p>
        </div>
        
        <!-- Mock Date Range or Controls -->
        <div class="flex items-center space-x-2 bg-slate-50 dark:bg-slate-700 rounded-lg p-1 border border-slate-200 dark:border-slate-600">
            <button class="px-3 py-1 text-xs font-medium rounded-md bg-white dark:bg-slate-600 shadow-sm text-slate-800 dark:text-white">7 Days</button>
            <button class="px-3 py-1 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">30 Days</button>
        </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        <!-- KPI Column -->
        <div class="lg:col-span-1 space-y-4">
            <div class="p-4 bg-slate-50 dark:bg-slate-700/30 rounded-lg border border-slate-100 dark:border-slate-600">
                <p class="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Events (24h)</p>
                <div class="mt-1 flex items-baseline">
                    <span class="text-2xl font-bold text-slate-900 dark:text-white">{{ formatNumber(overview.successfulEvents24h + overview.totalErrors24h) }}</span>
                </div>
            </div>
             <div class="p-4 bg-slate-50 dark:bg-slate-700/30 rounded-lg border border-slate-100 dark:border-slate-600">
                <p class="text-xs font-medium text-slate-500 uppercase tracking-wider">Active Pipelines</p>
                <div class="mt-1 flex items-baseline">
                    <span class="text-2xl font-bold text-slate-900 dark:text-white">{{ overview.activePipelines }}</span>
                    <span class="ml-2 text-xs text-slate-400">/ {{ overview.totalPipelines }}</span>
                </div>
            </div>
             <div class="p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-900/20">
                <p class="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wider">Errors (24h)</p>
                <div class="mt-1 flex items-baseline">
                    <span class="text-2xl font-bold text-red-700 dark:text-red-300">{{ overview.totalErrors24h }}</span>
                    <span class="ml-2 text-xs text-red-500">{{ (overview.errorRate24h * 100).toFixed(1) }}% Rate</span>
                </div>
            </div>
        </div>

        <!-- Main Chart -->
        <div class="lg:col-span-3 h-64">
             <Line v-if="chartData.labels" :data="chartData" :options="chartOptions" />
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  CategoryScale,
  LinearScale,
  Filler
} from 'chart.js'
import { Line } from 'vue-chartjs'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const props = defineProps<{
    overview: any
}>()

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(num);
}

const chartData = computed(() => {
    if (!props.overview.history) return {}
    
    return {
        labels: props.overview.history.labels,
        datasets: props.overview.history.datasets.map((ds: any) => ({
            label: ds.label,
            borderColor: ds.color,
            backgroundColor: ds.color + '20', // transparent fill
            data: ds.data,
            fill: true,
            tension: 0.4
        }))
    }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top' as const
    }
  },
  scales: {
    y: {
      grid: {
        color: 'rgba(200, 200, 200, 0.1)'
      }
    },
    x: {
        grid: {
            display: false
        }
    }
  }
}
</script>
